import {
    Modal,
    Box,
    Button,
    Typography,
    CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { ModalBaseStyles, ModalHeader } from "../../../../Common/styles/modal";
import { goalsApi } from "../../../../../utils/goalsApi";
import { errorToastMessage, toastMessage } from "../../../../../utils/toast";

interface DeleteGoalDialogProps {
    open: boolean;
    onClose: () => void;
    goalId: string | null;
    goalTitle?: string;
    onSuccess: () => void;
}

const DeleteGoalDialog = ({ open, onClose, goalId, goalTitle, onSuccess }: DeleteGoalDialogProps) => {
    const [loading, setLoading] = useState(false);

    const handleDelete = async () => {
        if (!goalId) return;

        try {
            setLoading(true);
            await goalsApi.deleteGoal(goalId);
            toastMessage("success", "Goal deleted successfully");
            onSuccess();
            onClose();
        } catch (error) {
            errorToastMessage(error as Error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ ...ModalBaseStyles, width: "500px", minHeight: "auto" }}>
                <ModalHeader title="Delete Goal" onCloseClick={onClose} />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="body1">
                        Are you sure you want to delete this goal?
                    </Typography>
                    {goalTitle && (
                        <Box
                            sx={{
                                bgcolor: "#f3f4f6",
                                p: 1.5,
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="body2" fontWeight="medium">
                                {goalTitle}
                            </Typography>
                        </Box>
                    )}
                    <Box
                        sx={{
                            bgcolor: "#fef2f2",
                            border: "1px solid #fecaca",
                            borderRadius: 1,
                            p: 1.5,
                        }}
                    >
                        <Typography variant="body2" color="error" sx={{ fontWeight: 500, mb: 0.5 }}>
                            ⚠️ Warning
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Deleting this goal will also delete all associated patient progress records. This action cannot be undone.
                        </Typography>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                        <Button variant="outlined" onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                            disabled={loading || !goalId}
                        >
                            {loading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                "Delete Goal"
                            )}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default DeleteGoalDialog;
